import React from 'react';
import { X, AlertCircle, CheckCircle, Clock, User, MapPin } from 'lucide-react';
import { api, endpoints } from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface AlertDetailModalProps {
  alert: {
    id: string;
    worker_id?: string;
    block_id?: string;
    type: string;
    priority: string;
    message: string;
    action_required: string;
    resolved: boolean;
    resolved_at?: string;
    resolved_by?: string;
    resolution_notes?: string;
    created_at: string;
  };
  onClose: () => void;
  onResolve?: () => void;
}

const AlertDetailModal: React.FC<AlertDetailModalProps> = ({ alert, onClose, onResolve }) => {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const resolveMutation = useMutation(
    async (resolutionNotes?: string) => {
      const response = await api.post(endpoints.alertResolve(alert.id), {
        resolution_notes: resolutionNotes
      });
      return response.data;
    },
    {
      onSuccess: () => {
        showSuccess('Alert resolved successfully');
        queryClient.invalidateQueries(['alerts']);
        if (onResolve) onResolve();
        onClose();
      },
      onError: (error: any) => {
        showError(error.response?.data?.detail || 'Failed to resolve alert');
      }
    }
  );

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-600';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-600';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-600';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-600';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-600';
    }
  };

  const formatAlertType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-white rounded shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AlertCircle className={`h-6 w-6 ${
              alert.priority === 'critical' ? 'text-red-600' :
              alert.priority === 'high' ? 'text-orange-600' :
              alert.priority === 'medium' ? 'text-yellow-600' :
              'text-blue-600'
            }`} />
            <h2 className="text-xl font-semibold text-gray-900">
              Alert Details
            </h2>
            {alert.resolved && (
              <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                Resolved
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Alert Type & Priority */}
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Alert Type</label>
              <p className="text-lg font-semibold text-gray-900">{formatAlertType(alert.type)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <span className={`px-3 py-1 rounded-full text-sm font-medium border-2 ${getPriorityColor(alert.priority)}`}>
                {alert.priority.toUpperCase()}
              </span>
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
            <p className="text-gray-900 bg-gray-50 p-4 rounded">{alert.message}</p>
          </div>

          {/* Action Required */}
          {alert.action_required && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Action Required</label>
              <p className="text-gray-900 bg-yellow-50 p-4 rounded border-l-4 border-yellow-500">
                {alert.action_required}
              </p>
            </div>
          )}

          {/* Alert Metadata */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
            <div className="flex items-start space-x-2">
              <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <label className="block text-xs font-medium text-gray-500">Created</label>
                <p className="text-sm text-gray-900">
                  {new Date(alert.created_at).toLocaleString()}
                </p>
              </div>
            </div>
            
            {alert.resolved && alert.resolved_at && (
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                <div>
                  <label className="block text-xs font-medium text-gray-500">Resolved</label>
                  <p className="text-sm text-gray-900">
                    {new Date(alert.resolved_at).toLocaleString()}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Resolution Notes */}
          {alert.resolution_notes && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Resolution Notes</label>
              <p className="text-gray-900 bg-green-50 p-4 rounded border-l-4 border-green-500">
                {alert.resolution_notes}
              </p>
            </div>
          )}

          {/* Resolve Button (if not resolved) */}
          {!alert.resolved && (
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={() => resolveMutation.mutate(undefined)}
                disabled={resolveMutation.isLoading}
                className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resolveMutation.isLoading ? 'Resolving...' : 'Mark as Resolved'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertDetailModal;

